import { useObserve } from "@legendapp/state/react";
import { Skeleton } from "@repo/ui/ui/skeleton";
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";

import { InvitationShellLayout } from "~/components/invitation-shell-layout";
import { OnboardStepLayout } from "~/pages/onboard/onboard-step-layout";
import { user$ } from "~/stores/auth";
import { activeOrganization$ } from "~/stores/organizations";

/**
 * Onboarding wizard layout. Each step lives in its own file route under
 * `/onboard/<step>` so that `beforeLoad` gates can decide synchronously what
 * the user should see -- eliminating the render-time step resolution that
 * the old single-page wizard used to do.
 *
 * Hosts the single in-session "navigate to /" observer that fires when
 * both columns (`user.onboarding` and the active `org.onboarding`) reach
 * `"done"` mid-flow (e.g. after invitation accept or the catalog continue
 * button). Individual step components must not navigate to `/` themselves
 * -- they advance the appropriate column via the server-first action and
 * this layout reacts.
 */
const OnboardLayout = () => {
  const navigate = useNavigate();

  useObserve(() => {
    const userDone = user$.onboarding.get() === "done";
    if (!userDone) return;

    const org = activeOrganization$.get();
    // Without an active org the user has nothing to onboard into; let
    // `_app/index` route them appropriately.
    if (!org?.id) {
      queueMicrotask(() => navigate({ to: "/", replace: true }));
      return;
    }
    if (org.onboarding === "done") {
      queueMicrotask(() => navigate({ to: "/", replace: true }));
    }
  });

  return (
    <InvitationShellLayout>
      <Outlet />
    </InvitationShellLayout>
  );
};

/**
 * Safety-net rendered when any `/onboard/*` sub-route's `beforeLoad` runs
 * longer than `defaultPendingMs`. Mirrors the split-step layout with skeleton
 * placeholders (title, description, primary action on the left; one large
 * content box on the right) so the loading state matches the real step chrome.
 */
const OnboardPending = () => (
  <InvitationShellLayout>
    <OnboardStepLayout
      title={<Skeleton className="h-8 w-48" />}
      description={
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      }
      primaryAction={<Skeleton className="h-11 w-full" />}
    >
      <Skeleton className="min-h-[300px] w-full flex-1" />
    </OnboardStepLayout>
  </InvitationShellLayout>
);

export const Route = createFileRoute("/onboard")({
  beforeLoad: ({ context }) => {
    if (!context.user) {
      throw redirect({ to: "/login", replace: true });
    }
  },
  component: OnboardLayout,
  pendingComponent: OnboardPending,
});
